import { useEffect, useState, type FormEvent } from "react";
import { api, type AuthUser } from "./api";
import { useGame } from "./store";

export type AuthMode = "login" | "register";

type Props = {
  initialMode?: AuthMode;
  /** Nach erfolgreichem Login/Register */
  onAuth: (token: string, username: string, user: AuthUser) => void;
  /** Optional: Link zurück zur Landing */
  showHomeLink?: boolean;
  /** Kompaktere Überschrift auf der Landing */
  embedded?: boolean;
};

export function AuthCard({
  initialMode = "login",
  onAuth,
  showHomeLink = false,
  embedded = false,
}: Props) {
  const setPanel = useGame((s) => s.setPanel);
  const isLocal =
    typeof window !== "undefined" &&
    /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname);
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [login, setLogin] = useState(isLocal ? "dev" : "");
  const [password, setPassword] = useState(isLocal ? "devtest12" : "");
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);

  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  useEffect(() => {
    if (!isLocal) return;
    api
      .health()
      .then((h: { devLogin?: { login: string; password?: string } }) => {
        if (h?.devLogin?.login) {
          setLogin(h.devLogin.login);
          setPassword(h.devLogin.password ?? "devtest12");
          setMode("login");
        }
      })
      .catch(() => null);
  }, [isLocal]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (mode === "register" && !acceptTerms) {
      setError("Bitte AGB / Datenschutz bestätigen.");
      return;
    }
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      if (mode === "register") {
        const res = await api.register({
          email,
          username,
          password,
          acceptTerms: true,
        });
        if (res.message) setInfo(res.message);
        onAuth(res.token, res.user.username, res.user);
      } else {
        const res = await api.login(login, password);
        onAuth(res.token, res.user.username, res.user);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(
        msg === "Failed to fetch"
          ? "API nicht erreichbar. Läuft die API? Seite neu laden."
          : msg,
      );
    } finally {
      setBusy(false);
    }
  }

  function requestForgot() {
    const id = login.trim();
    if (!id) {
      setInfo("Bitte E-Mail oder Benutzername eingeben, dann erneut „Passwort vergessen?“.");
      return;
    }
    setBusy(true);
    setError(null);
    void api
      .forgotPassword(id.includes("@") ? id : id)
      .then((r) => setInfo(r.message))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setBusy(false));
  }

  return (
    <form className={`auth-card${embedded ? " auth-card-embedded" : ""}`} onSubmit={submit}>
      {!embedded && <div className="eyebrow">TESTPHASE</div>}
      <h1>{embedded ? (mode === "login" ? "Anmelden" : "Registrieren") : "BOS Dispatch Architect"}</h1>
      <p className="lede">
        {embedded
          ? mode === "login"
            ? "Mit bestehendem Account weiterspielen — danach Spielstand wählen."
            : "Neuen Account anlegen. Spielen ggf. erst nach Admin-Freigabe."
          : "Private Leitstellen-Simulation. Login zuerst; Registrierung nur wenn nötig. Kein echter Notruf."}
      </p>

      <div className="auth-mode-tabs" role="tablist" aria-label="Anmelden oder Registrieren">
        <button
          type="button"
          role="tab"
          aria-selected={mode === "login"}
          className={mode === "login" ? "active" : ""}
          onClick={() => {
            setMode("login");
            setError(null);
            setInfo(null);
          }}
        >
          Anmelden
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === "register"}
          className={mode === "register" ? "active" : ""}
          onClick={() => {
            setMode("register");
            setError(null);
            setInfo(null);
          }}
        >
          Registrieren
        </button>
      </div>

      {isLocal && mode === "login" && (
        <p className="muted small" style={{ marginBottom: 8 }}>
          Lokal vorausgefüllt: <code>dev</code> / <code>devtest12</code>
        </p>
      )}
      {mode === "register" ? (
        <>
          <div className="field">
            <label>E-Mail</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              required
            />
          </div>
          <div className="field">
            <label>Benutzername (öffentlich im Spiel)</label>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              minLength={3}
              maxLength={32}
              pattern="[a-zA-Z0-9_\-äöüÄÖÜß. ]{3,32}"
              title="3–32 Zeichen: Buchstaben, Zahlen, Punkt, Unterstrich, Bindestrich, Leerzeichen"
            />
            <span className="muted small">
              Buchstaben, Zahlen, . _ - und Leerzeichen — keine Sonderzeichen wie @
            </span>
          </div>
        </>
      ) : (
        <div className="field">
          <label>E-Mail oder Benutzername</label>
          <input value={login} onChange={(e) => setLogin(e.target.value)} autoFocus required />
        </div>
      )}
      <div className="field">
        <label>Passwort {mode === "register" ? "(min. 8)" : ""}</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={mode === "register" ? 8 : 1}
        />
      </div>
      {mode === "login" && (
        <p className="muted small">
          <button type="button" className="linkish" disabled={busy} onClick={requestForgot}>
            Passwort vergessen?
          </button>
          <span className="muted"> · ohne SMTP: Admin/Support · siehe docs/SMTP.md</span>
        </p>
      )}
      {mode === "register" && (
        <label className="legal-check">
          <input
            type="checkbox"
            checked={acceptTerms}
            onChange={(e) => setAcceptTerms(e.target.checked)}
          />
          <span>
            Ich akzeptiere die{" "}
            <button type="button" className="linkish" onClick={() => setPanel("legal")}>
              AGB & Datenschutz
            </button>{" "}
            und bestätige den Jugendschutz-Hinweis (Simulation, kein Notruf).
          </span>
        </label>
      )}
      <div className="btn-row">
        <button className="btn primary" disabled={busy} type="submit">
          {mode === "login" ? "Anmelden" : "Account erstellen"}
        </button>
      </div>
      {info && <p className="muted">{info}</p>}
      {error && <p className="error">{error}</p>}
      <p className="muted small" style={{ marginTop: 12 }}>
        <button type="button" className="linkish" onClick={() => setPanel("legal")}>
          Impressum · Datenschutz · AGB
        </button>
        {showHomeLink && (
          <>
            <span className="muted"> · </span>
            <a className="linkish" href="/">
              Zur Startseite
            </a>
          </>
        )}
      </p>
    </form>
  );
}
